/**
 * What a contribution adds, worked out the way the API cuts it.
 *
 * "Up to this segment" sends everything from the start of the document to the
 * picked segment. The API packs that into clips of at most 30 seconds, starting
 * a new clip at any silence over 3 seconds (``training_clip_windows.py``, which
 * mirrors stadiem's ``whisper_windows``). The dialog uses the same rules so it
 * can say what will be added before the customer clicks. Keep the numbers in
 * step with the API.
 */
export const CLIP_WINDOW_MS = 30_000;
export const CLIP_MAX_GAP_MS = 3_000;
export const CLIP_MIN_MS = 1_000;
export const MAX_CONTRIBUTION_MS = 20 * 60_000;

export type Span = { start: number; stop: number };

export type ContributionSummary = {
  /** Clips the API will create (before skipping ones already contributed). */
  clips: number;
  /** Audio those clips cover, in milliseconds. */
  ms: number;
  /** A single segment is over 30s, so it will be left out. */
  hasOverlongSegment: boolean;
};

export const packSpans = (spans: Span[]): Span[][] => {
  const clips: Span[][] = [];
  let current: Span[] = [];
  for (const span of spans) {
    if (span.stop - span.start > CLIP_WINDOW_MS) {
      if (current.length) clips.push(current);
      current = [];
      continue;
    }
    if (
      current.length &&
      (span.stop - current[0].start > CLIP_WINDOW_MS ||
        span.start - current[current.length - 1].stop > CLIP_MAX_GAP_MS)
    ) {
      clips.push(current);
      current = [];
    }
    current.push(span);
  }
  if (current.length) clips.push(current);
  return clips;
};

export const summarizeContribution = (spans: Span[]): ContributionSummary => {
  const clips = packSpans(spans)
    .map(clip => clip[clip.length - 1].stop - clip[0].start)
    .filter(ms => ms >= CLIP_MIN_MS);
  return {
    clips: clips.length,
    ms: clips.reduce((total, ms) => total + ms, 0),
    hasOverlongSegment: spans.some(s => s.stop - s.start > CLIP_WINDOW_MS)
  };
};

/** "1m 58s", "45s". */
export const formatClipDuration = (ms: number): string => {
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes === 0) return `${seconds}s`;
  return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
};

/**
 * Language keys transcribed with per-utterance detection ("Multilingual
 * (auto-detect)"). Mirrors ``MULTILINGUAL_LANGUAGE_KEYS`` in the API. Such a
 * session records no detected language, so it cannot be matched to a model.
 */
const MULTILINGUAL_LANGUAGE_KEYS = new Set(['mul']);

export const isMultilingualLanguage = (key?: string | null): boolean =>
  !!key && MULTILINGUAL_LANGUAGE_KEYS.has(key.toLowerCase());
